import { Fragment, useState, useEffect, useMemo, useRef } from 'react'
import { Combobox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/solid'
import { useTranslation } from 'react-i18next'

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export const ComboboxPackageSelect = ({
    options = [],
    value = null,
    className = "",
    optionsStyles = "",
    onChange = () => {},
    autoFocusOnMount = false,
    autoFocusSignal = 0
}) => {
    const { t } = useTranslation()
    const optionsArray = Array.isArray(options) ? options : []
    const [selected, setSelected] = useState(value)
    const [query, setQuery] = useState(value?.name || '')
    const [isOpen, setIsOpen] = useState(false)
    const inputRef = useRef(null)
    const hasAutofocusedRef = useRef(false)
    const toggleButtonRef = useRef(null)
    const isClosingRef = useRef(false)
    const lastAutoFocusSignal = useRef(autoFocusSignal)

    useEffect(() => {
        setSelected(value)
        setQuery(value?.name || '')
    }, [value])

    useEffect(() => {
        if (autoFocusOnMount && !hasAutofocusedRef.current) {
            hasAutofocusedRef.current = true
            setTimeout(() => {
                inputRef.current?.focus()
                // Select pre-filled text so a scanned barcode replaces it instead of concatenating (BUGS-6401)
                inputRef.current?.select()
            }, 0)
        }
    }, [autoFocusOnMount])

    useEffect(() => {
        const hasNewSignal = autoFocusSignal > 0 && autoFocusSignal !== lastAutoFocusSignal.current
        if (!hasNewSignal) {
            return
        }
        lastAutoFocusSignal.current = autoFocusSignal
        requestAnimationFrame(() => {
            inputRef.current?.focus()
            // Select pre-filled text so a scanned barcode replaces it instead of concatenating (BUGS-6401)
            inputRef.current?.select()
        })
    }, [autoFocusSignal])

    const findBarcodeMatch = (searchQuery) => {
        if (!searchQuery || !optionsArray || optionsArray.length === 0) return null

        const trimmedQuery = searchQuery.toLowerCase().trim()

        for (const option of optionsArray) {
            if (!option) continue
            const optionBarCode = (option.bar_code || '').trim()

            if (optionBarCode && optionBarCode.toLowerCase() === trimmedQuery) {
                return option
            }
        }

        return null
    }

    // A handheld scanner emits its payload as one keystroke burst, wherever the
    // caret happens to be. When the field still holds the previously selected
    // package, the burst is merged into it instead of replacing it -- appended
    // ("CX01" + "CX03") or prepended, depending on where focus left the caret.
    // The exact match above then misses and the packer keeps the old box.
    // Selecting the pre-filled text on focus only narrows that window, it can't
    // close it: the focus runs in a deferred callback the burst can beat.
    //
    // By Enter the burst is complete, so strip the one token we know is stale --
    // the selected package's name -- off whichever end it sits on, and require
    // an exact barcode match on what the scanner actually contributed. Matching
    // a bare trailing substring instead would be unsafe: `name` and `bar_code`
    // are independent free-text fields, so an unrelated name ending in another
    // package's barcode would silently switch the box on a plain Enter.
    const findScannedBarcodeMatch = (searchQuery) => {
        const staleName = (selected?.name || '').trim().toLowerCase()
        if (!staleName || !searchQuery) return null

        const trimmedQuery = searchQuery.toLowerCase().trim()
        let remainder = null

        if (trimmedQuery.startsWith(staleName)) {
            remainder = trimmedQuery.slice(staleName.length)
        } else if (trimmedQuery.endsWith(staleName)) {
            remainder = trimmedQuery.slice(0, trimmedQuery.length - staleName.length)
        }

        // Nothing left over means the field was never scanned into -- a plain
        // Enter on the unchanged selection. That must not resolve to anything.
        if (!remainder || !remainder.trim()) return null

        return findBarcodeMatch(remainder)
    }

    const filteredOptions = useMemo(() => {
        if (!optionsArray || optionsArray.length === 0) {
            return []
        }

        if (!query) {
            return optionsArray
        }

        const lowerQuery = query.toLowerCase().trim()
        return optionsArray.filter((option) => {
            if (!option) return false
            const optionName = (option.name || '').toLowerCase()
            const optionBarCode = (option.bar_code || '').trim().toLowerCase()
            return optionName.includes(lowerQuery) || (optionBarCode && optionBarCode.includes(lowerQuery))
        })
    }, [optionsArray, query])

    const handleOnChange = (newValue) => {
        const nextQuery = newValue?.name || ''
        setSelected(newValue)
        setQuery(nextQuery)

        onChange(newValue)

        // ensure dropdown stays closed after hitting enter key
        if (isClosingRef.current) {
            requestAnimationFrame(() => {
                setIsOpen(false)
            })
        }
    }

    const closeDropdown = () => {
        isClosingRef.current = true
        inputRef.current?.blur()
        requestAnimationFrame(() => {
            const isExpanded = toggleButtonRef.current?.getAttribute('aria-expanded') === 'true'
            if (isExpanded) {
                toggleButtonRef.current?.click()
            }
            setTimeout(() => {
                isClosingRef.current = false
            }, 150)
        })
    }

    const handleInputChange = (event) => {
        const newQuery = event.target.value
        setQuery(newQuery)
        const barcodeMatch = findBarcodeMatch(newQuery)
        if (barcodeMatch) {
            handleOnChange(barcodeMatch)
            closeDropdown()
        }
    }

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            event.stopPropagation()
            const trimmedQuery = query.toLowerCase().trim()

            if (!trimmedQuery) {
                setIsOpen(false)
                inputRef.current?.blur()
                return
            }

            const barcodeMatch = findBarcodeMatch(trimmedQuery) || findScannedBarcodeMatch(trimmedQuery)

            if (barcodeMatch) {
                isClosingRef.current = true
                setIsOpen(false)
                inputRef.current?.blur()
                handleOnChange(barcodeMatch)
                setTimeout(() => {
                    isClosingRef.current = false
                }, 200)
                return
            }

            // Nothing resolved. If the text still matches something the packer
            // could be part-way through typing or arrowing to, leave it alone.
            // Only when it matches nothing at all is it scanner debris: empty
            // the field so the miss is visible and the rescan starts clean
            // rather than piling onto the residue and missing again.
            //
            // The clear is deliberately local. The parent's change handler reads
            // package_type straight off the new package, so emitting a null here
            // would crash the packing screen; the previously selected package
            // therefore stays in the parent until a rescan lands.
            if (filteredOptions.length === 0) {
                setQuery('')
                setSelected(null)
            }
            setIsOpen(false)
            inputRef.current?.blur()
        }
    }

    return (
        <div className={className}>
            <Combobox value={selected} onChange={handleOnChange} open={isOpen} onClose={() => {
                if (!isClosingRef.current) {
                    setIsOpen(false)
                }
            }}>
                {({ open }) => {
                    // Sync the local isOpen with Headless UI's internal open
                    // state (e.g., closed via ESC / outside click). Defer to
                    // a microtask so we don't setState during render (React
                    // 18 warns loudly about that: "Cannot update a component
                    // while rendering a different component").
                    if (!isClosingRef.current && open !== isOpen) {
                        queueMicrotask(() => {
                            if (!isClosingRef.current) setIsOpen(open)
                        })
                    }
                    return (
                        <div
                            className="relative mt-1"
                            onMouseDown={(event) => {
                                if (event.button !== 0) return
                                if (event.target === toggleButtonRef.current) return
                                if (event.target.closest('[role="option"]')) return
                                event.preventDefault()
                                if (isClosingRef.current) {
                                    return
                                }
                                if (!open) {
                                    setQuery('')
                                    setSelected(null)
                                    setIsOpen(true)
                                }
                                requestAnimationFrame(() => inputRef.current?.focus())
                            }}
                        >
                            <div className="relative w-full overflow-hidden rounded-md bg-white text-left shadow-sm focus:outline-none sm:text-sm border border-gray-300 cursor-pointer focus-within:cursor-text focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 focus-within:ring-offset-white">
                                <Combobox.Input
                                    ref={inputRef}
                                    className="w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 cursor-pointer focus:cursor-text focus:ring-0"
                                    displayValue={(option) => option?.name || query}
                                    autoComplete="off"
                                    onClick={(event) => {
                                        if (isClosingRef.current) {
                                            return
                                        }
                                        if (toggleButtonRef.current) {
                                            event.stopPropagation()
                                            requestAnimationFrame(() => {
                                                toggleButtonRef.current?.click()
                                            })
                                        }
                                    }}
                                    onFocus={() => {
                                        // Don't auto-open if we're in the process of closing
                                        if (isClosingRef.current) {
                                            return
                                        }
                                        if (!open) {
                                            setIsOpen(true)
                                        }
                                    }}
                                    onChange={handleInputChange}
                                    onKeyDown={handleKeyDown}
                                    placeholder={t('generate_shipment.shipping_package_data.search_placeholder')}
                                />
                                <Combobox.Button
                                    ref={toggleButtonRef}
                                    className="absolute inset-y-0 right-0 flex items-center pr-2"
                                >
                                    <ChevronDownIcon
                                        className="h-5 w-5 text-gray-400"
                                        aria-hidden="true"
                                    />
                                </Combobox.Button>
                            </div>
                            <Transition
                                show={isOpen && !isClosingRef.current}
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                            >
                                <Combobox.Options className="absolute z-50 mt-1 max-h-80 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                    {filteredOptions.length === 0 && query !== '' ? (
                                        <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                                            {t('general.messages.no_options')}
                                        </div>
                                    ) : (
                                        filteredOptions.map((option, index) => {
                                            if (!option) return null
                                            const optionId = option.id != null ? option.id : `option-${index}`
                                            const optionName = option.name || ''
                                            const optionKey = `${optionId}-${optionName || index}`

                                            return (
                                                <Combobox.Option
                                                    key={optionKey}
                                                    className={({ active }) =>
                                                        classNames(
                                                            active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                                                            'relative cursor-default select-none py-2 pl-10 pr-4'
                                                        )
                                                    }
                                                    value={option}
                                                >
                                                    {({ selected, active }) => (
                                                        <>
                                                            <span className={classNames(selected ? 'font-semibold' : 'font-normal', 'block truncate', optionsStyles)}>
                                                                {optionName}
                                                            </span>
                                                            {selected && (
                                                                <span className={classNames(active ? 'text-white' : 'text-indigo-600', 'absolute inset-y-0 left-0 flex items-center pl-3', optionsStyles)}>
                                                                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                                </span>
                                                            )}
                                                        </>
                                                    )}
                                                </Combobox.Option>
                                            )
                                        })
                                    )}
                                </Combobox.Options>
                            </Transition>
                        </div>
                    )
                }}
            </Combobox>
        </div>
    )
}
